import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { MAILBOX_TAG_DISPLAY_STORAGE_KEY as STORAGE_KEY } from 'src/app/shared/preserved-storage-keys';
import {
  DEFAULT_MAILBOX_TAG_DISPLAY,
  MAILBOX_TAG_DISPLAY_OPTIONS,
  MailboxTagDisplay,
} from '../models/inbox.models';


/**
 * Where the recipient-mailbox tag is shown, as chosen in Settings → Smart Inbox.
 *
 * Client-side only, like the timeline view preference: the tag is a display aid
 * with no server-side meaning, and there is no user-preferences endpoint to put
 * it behind. Consequence to know: the choice does not follow the user to another
 * browser or device. It does survive a logout — the key is registered in
 * `PRESERVED_STORAGE_KEYS`, so an involuntary 401 cannot silently reset it.
 */
@Injectable({ providedIn: 'root' })
export class MailboxTagPreferenceService {
  private readonly display$ = new BehaviorSubject<MailboxTagDisplay>(this.read());

  /** Current preference, emitting on every change. */
  get changes(): Observable<MailboxTagDisplay> {
    return this.display$.asObservable();
  }

  get current(): MailboxTagDisplay {
    return this.display$.value;
  }

  set(display: MailboxTagDisplay): void {
    if (display === this.display$.value) return;
    this.display$.next(display);
    try {
      localStorage.setItem(STORAGE_KEY, display);
    } catch {
      // Ignore storage failures — the choice still applies for this session.
    }
  }

  /**
   * Whether the tag may render on conversation lists.
   *
   * Reads `current` rather than taking the value as an argument: callers subscribe to
   * `changes` to know when to re-read, and passing the same value back in was two
   * sources of truth for one setting.
   */
  showOnList(): boolean {
    return this.current !== MailboxTagDisplay.HIDDEN;
  }

  /** Whether the tag may render in the open conversation's header. */
  showOnHeader(): boolean {
    return this.current === MailboxTagDisplay.BOTH;
  }

  private read(): MailboxTagDisplay {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && (MAILBOX_TAG_DISPLAY_OPTIONS as string[]).includes(stored)) {
        return stored as MailboxTagDisplay;
      }
    } catch {
      // Ignore storage failures — fall back to the default.
    }
    return DEFAULT_MAILBOX_TAG_DISPLAY;
  }
}
